/**
 * Phase 2 — IndexedDB persistence layer for offline drafts and the outbox
 * sync queue.
 *
 * Four object stores live in a single database:
 *  - "drafts": in-progress form data that hasn't been submitted yet (or was
 *    submitted while offline). Keyed by an arbitrary string id built from
 *    report/section/entity, e.g. "report:64f...:site-overview".
 *  - "outbox": queued network operations (form saves, file uploads) that
 *    failed because the device was offline. Each item stores enough to
 *    replay the request once connectivity returns.
 *  - "uploadSessions": in-progress resumable multipart S3 uploads (Phase
 *    4). Tracks which parts of a large file have already reached S3 so an
 *    interrupted upload resumes instead of restarting from byte zero.
 *  - "cache": saved audit data and image data for offline access.
 *
 * This is a thin native-IndexedDB wrapper (no external dependency) so it
 * works in every Chromium/Android browser this app targets.
 */

const DB_NAME = "encoaudit-offline"
// Initial released schema includes all four stores. Increase this only when
// stores, indexes, or key paths change; feature updates alone do not need a bump.
// Existing pre-release version-2 databases must be reset after syncing first.
const DB_VERSION = 1
const DRAFTS_STORE = "drafts"
const OUTBOX_STORE = "outbox"
const UPLOAD_SESSIONS_STORE = "uploadSessions"
const CACHE_STORE = "cache"

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this environment"))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(DRAFTS_STORE)) {
        db.createObjectStore(DRAFTS_STORE, { keyPath: "id" })
      }
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const outboxStore = db.createObjectStore(OUTBOX_STORE, { keyPath: "id" })
        outboxStore.createIndex("status", "status", { unique: false })
        outboxStore.createIndex("createdAt", "createdAt", { unique: false })
      }
      if (!db.objectStoreNames.contains(UPLOAD_SESSIONS_STORE)) {
        db.createObjectStore(UPLOAD_SESSIONS_STORE, { keyPath: "id" })
      }
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: "key" })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  return dbPromise
}

function runTransaction(storeName, mode, executor) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, mode)
        const store = tx.objectStore(storeName)
        let result
        try {
          result = executor(store)
        } catch (error) {
          reject(error)
          return
        }
        tx.oncomplete = () => resolve(result)
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction aborted"))
      }),
  )
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// ---------- Drafts ----------

export async function putDraft(draft) {
  const record = { ...draft, updatedAt: Date.now() }
  await runTransaction(DRAFTS_STORE, "readwrite", (store) => store.put(record))
  return record
}

export async function getDraft(id) {
  return runTransaction(DRAFTS_STORE, "readonly", (store) => requestToPromise(store.get(id))).then((p) => p)
}

export async function deleteDraft(id) {
  return runTransaction(DRAFTS_STORE, "readwrite", (store) => store.delete(id))
}

export async function getAllDrafts() {
  return runTransaction(DRAFTS_STORE, "readonly", (store) => requestToPromise(store.getAll())).then(
    (p) => p || [],
  )
}

// ---------- Outbox ----------

export async function putOutboxItem(item) {
  const record = {
    status: "pending",
    retryCount: 0,
    createdAt: Date.now(),
    ...item,
  }
  await runTransaction(OUTBOX_STORE, "readwrite", (store) => store.put(record))
  return record
}

export async function updateOutboxItem(id, patch) {
  return runTransaction(OUTBOX_STORE, "readwrite", async (store) => {
    const existing = await requestToPromise(store.get(id))
    if (!existing) return null
    const updated = { ...existing, ...patch, updatedAt: Date.now() }
    store.put(updated)
    return updated
  })
}

export async function deleteOutboxItem(id) {
  return runTransaction(OUTBOX_STORE, "readwrite", (store) => store.delete(id))
}

export async function getAllOutboxItems() {
  return runTransaction(OUTBOX_STORE, "readonly", (store) => requestToPromise(store.getAll())).then(
    (p) => (p || []).sort((a, b) => a.createdAt - b.createdAt),
  )
}

// ---------- Upload sessions (Phase 4 resumable multipart) ----------

export async function putUploadSession(session) {
  const record = { ...session, updatedAt: Date.now() }
  await runTransaction(UPLOAD_SESSIONS_STORE, "readwrite", (store) => store.put(record))
  return record
}

export async function getUploadSession(id) {
  return runTransaction(UPLOAD_SESSIONS_STORE, "readonly", (store) => requestToPromise(store.get(id)))
}

export async function deleteUploadSession(id) {
  return runTransaction(UPLOAD_SESSIONS_STORE, "readwrite", (store) => store.delete(id))
}

export async function getAllUploadSessions() {
  return runTransaction(UPLOAD_SESSIONS_STORE, "readonly", (store) => requestToPromise(store.getAll())).then(
    (p) => p || [],
  )
}

/**
 * Attempts to persist storage so the browser is less likely to evict
 * IndexedDB data under storage pressure (Phase 3 will also rely on this).
 */
export async function putCachedData(key, data) {
  const record = { key, data, updatedAt: Date.now() }
  await runTransaction(CACHE_STORE, "readwrite", (store) => store.put(record))
  return data
}

export async function getCachedData(key) {
  const record = await runTransaction(CACHE_STORE, "readonly", (store) => requestToPromise(store.get(key)))
  return record?.data ?? null
}

export async function deleteCachedData(key) {
  return runTransaction(CACHE_STORE, "readwrite", (store) => store.delete(key))
}

export async function requestPersistentStorage() {
  try {
    if (navigator?.storage?.persist) {
      return await navigator.storage.persist()
    }
  } catch (error) {
    console.log("[v0] Persistent storage request failed:", error?.message)
  }
  return false
}
